/** Selling-AI (v1 selling-ai.js) — the seller-side tools that live on a
 * transaction: a repair tracker, a printable Completion Report, and a Florida
 * Seller's Property Disclosure builder (+ printable document). All state rides
 * on the deal's edits (v1 card.edits), written through the live deals door via
 * opEditDealBlob — no new migration, faithful to where v1 stored it.
 *
 * The AI parts (addendum-parse / invoice-parse / MLS remarks — /api/selling/*)
 * are a separate gated round; this ships the fully-offline core first. */

import { backendFetch } from "../../lib/backend";
import { moneyExact } from "../../lib/money";

export interface SelRepair { id: string; t: string; cat: string; who: string; st: string; cost: number; desc: string; }
export interface SelInvoice { id: string; vendor: string; amount: number; desc: string; linked: string[]; }
/** One imported Repair-Addendum PDF (v1 E.selAddendums entry): file name,
 * how many repair items it added, when. */
export interface SelAddendum { name: string; n: number; at: number; }

// ---- MLS Public Remarks (v1 selling-ai.js sellingRemarksPanel) --------------
// v1 generated these via a backend AI call (/api/selling/remarks), which is a
// gated round. This builds strong remarks DETERMINISTICALLY from the same facts
// (v1 _selRemarksFacts) — always available, no backend, no gate. The output is
// fully editable and saved to the deal's selRemarks blob {text,tone,len,notes,at}.

export type RemarksTone = "Standard" | "Warm" | "Concise";
export type RemarksLength = "Short" | "Standard" | "Long";
export const REMARKS_TONES: { tone: RemarksTone; sub: string }[] = [
  { tone: "Standard", sub: "MLS-pro" }, { tone: "Warm", sub: "Inviting" }, { tone: "Concise", sub: "Just facts" },
];
export const REMARKS_LENGTHS: { len: RemarksLength; sub: string }[] = [
  { len: "Short", sub: "~75w" }, { len: "Standard", sub: "~150w" }, { len: "Long", sub: "~250w" },
];

export interface RemarksFacts {
  address: string; beds: string; baths: string; sqft: string; year: string;
  roof: string; hvac: string; hwh: string; hoa: string; highlights: string[];
}

export interface SelRemarks { text: string; tone: RemarksTone; len: RemarksLength; notes: string; at: number; }

/** v1 _selRemarksFacts — pull from the deal card fields (pp_* property, r_*
 * systems, t_hoa* HOA). renovation highlights come from the SOW item names. */
export function remarksFacts(
  deal: { address: string; fields: Record<string, unknown> },
  sowItemNames: string[] = [],
): RemarksFacts {
  const g = (k: string) => (typeof deal.fields[k] === "string" ? (deal.fields[k] as string).trim() : "");
  const hoa = [g("t_hoaco"), g("t_hoafee")].filter(Boolean).join(" · ");
  // v1 KEY regex: only real renovation line items become highlights, de-duped
  const KEY = /kitchen|bath|roof|hvac|floor|paint|cabinet|counter|appliance|window|door|landscap|pool|electric|plumb|vanity|shower|tile/i;
  const seen = new Set<string>();
  const highlights: string[] = [];
  for (const raw of sowItemNames) {
    const t = String(raw || "").trim();
    if (!t || !KEY.test(t)) continue;
    const k = t.toLowerCase().slice(0, 30);
    if (seen.has(k)) continue;
    seen.add(k);
    if (highlights.length < 18) highlights.push(t.slice(0, 80));
  }
  return {
    address: deal.address,
    beds: g("pp_beds"), baths: g("pp_baths"), sqft: g("pp_sqft"), year: g("pp_yr"),
    roof: g("r_roof"), hvac: g("r_hvac"), hwh: g("r_hwh"), hoa, highlights,
  };
}

export function narrowSelRemarks(raw: unknown): SelRemarks | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o["text"] !== "string") return null;
  const tone = o["tone"] === "Warm" || o["tone"] === "Concise" ? o["tone"] : "Standard";
  const len = o["len"] === "Short" || o["len"] === "Long" ? o["len"] : "Standard";
  return { text: o["text"], tone, len, notes: typeof o["notes"] === "string" ? o["notes"] : "", at: num(o["at"]) };
}

const oxford = (items: string[]): string =>
  items.length <= 1 ? (items[0] ?? "") : items.length === 2 ? `${items[0]} and ${items[1]}` : `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;

/** Deterministic MLS remarks from the gathered facts. Tone shapes the voice;
 * length targets ~75 / ~150 / ~250 words by how many detail sentences it keeps. */
export function buildRemarks(facts: RemarksFacts, tone: RemarksTone, length: RemarksLength, notes = ""): string {
  const city = (facts.address.split(",")[1] ?? "").trim();
  const bb: string[] = [];
  if (facts.beds) bb.push(`${facts.beds}-bedroom`);
  if (facts.baths) bb.push(`${facts.baths}-bath`);
  const bbStr = bb.join(", ");
  const sqftStr = facts.sqft ? `${facts.sqft} sqft` : "";
  const yearStr = facts.year ? `built in ${facts.year}` : "";

  // Opening line by tone
  const homeNoun = bbStr ? `${bbStr} home` : "home";
  let open: string;
  if (tone === "Concise") {
    open = [bbStr ? bbStr.replace(", ", " / ") : "", sqftStr, facts.year].filter(Boolean).join(" · ") || "Investor-ready home.";
  } else if (tone === "Warm") {
    open = `Welcome home! This inviting ${homeNoun}${sqftStr ? ` offers ${sqftStr} of comfortable living space` : ""}${yearStr ? `, ${yearStr}` : ""}${city ? `, in the heart of ${city}` : ""}.`;
  } else {
    open = `Beautifully updated ${homeNoun}${sqftStr ? ` offering ${sqftStr} of living space` : ""}${yearStr ? `, ${yearStr}` : ""}${city ? ` in ${city}` : ""}.`;
  }

  const sentences: string[] = [];

  // Renovation highlights
  if (facts.highlights.length) {
    const take = length === "Short" ? 3 : length === "Long" ? 10 : 6;
    const hl = oxford(facts.highlights.slice(0, take).map((h) => h.toLowerCase()));
    sentences.push(tone === "Concise" ? `Updates: ${oxford(facts.highlights.slice(0, take))}.` : `Recent improvements include ${hl}.`);
  }

  // Systems
  const sys: string[] = [];
  if (facts.roof) sys.push(`roof (${facts.roof})`);
  if (facts.hvac) sys.push(`HVAC (${facts.hvac})`);
  if (facts.hwh) sys.push(`water heater (${facts.hwh})`);
  if (sys.length && length !== "Short") {
    sentences.push(tone === "Concise" ? `Systems: ${oxford(sys)}.` : `Major systems have been addressed — ${oxford(sys)}.`);
  }

  // HOA
  if (facts.hoa && length === "Long") sentences.push(tone === "Concise" ? `HOA: ${facts.hoa}.` : `The community is served by an HOA (${facts.hoa}).`);

  // Extra notes
  if (notes.trim()) sentences.push(notes.trim());

  // Long-form filler (only for Long + non-Concise, when there's room)
  if (length === "Long" && tone !== "Concise") {
    sentences.push(city ? `Enjoy an ideal location with easy access to ${city}-area shopping, dining, and major commuter routes.` : "Enjoy an ideal location with easy access to shopping, dining, and major commuter routes.");
  }

  // Closing CTA
  let cta: string;
  if (tone === "Concise") cta = "Available now — cash or conventional.";
  else if (tone === "Warm") cta = "Come see everything this home has to offer — schedule your private tour today!";
  else cta = "Move-in ready and priced to sell — schedule your private showing today.";

  const body = [open, ...sentences, cta].filter(Boolean).join(tone === "Concise" ? " " : " ");
  return body;
}

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown): number => { const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")); return Number.isFinite(n) ? n : 0; };

let _rid = 0;
export const repairId = (): string => "rp" + Date.now().toString(36) + (_rid++).toString(36);

export const REPAIR_CATS = ["General", "Roof", "HVAC", "Plumbing", "Electrical", "Kitchen", "Bath", "Flooring", "Paint", "Exterior", "Landscaping"];
export const REPAIR_TRADES = ["General Contractor", "Roofer", "HVAC Tech", "Plumber", "Electrician", "Handyman", "Painter", "Flooring", "Landscaper"];
export const REPAIR_STATUS = ["Pending", "In Progress", "Completed"];

export function narrowRepair(raw: unknown): SelRepair {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return { id: str(o["id"]) || repairId(), t: str(o["t"]), cat: str(o["cat"]) || "General", who: str(o["who"]) || "General Contractor", st: str(o["st"]) || "Pending", cost: num(o["cost"]), desc: str(o["desc"]) };
}
export function narrowInvoice(raw: unknown): SelInvoice {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const linked = o["linked"];
  return { id: str(o["id"]) || repairId(), vendor: str(o["vendor"]), amount: num(o["amount"]), desc: str(o["desc"]), linked: Array.isArray(linked) ? linked.map(str) : [] };
}

// ---- AI parse (server /api/selling/*) — gated by liveFire `sellingAi` --------

/** One repair the addendum parser found (v1 addendum-parse j.repairs[]). */
export interface ParsedRepair { title: string; category: string; contractor: string; }

/** v1 selAddendumPick: PDF (data URL) → repair items. */
export async function parseAddendum(token: string | null, pdfB64: string, signal?: AbortSignal): Promise<ParsedRepair[]> {
  const j = await backendFetch(token, "/api/selling/addendum-parse", {
    method: "POST", body: { pdf_b64: pdfB64 }, timeoutMs: 120000, signal,
  });
  const o = j && typeof j === "object" ? (j as Record<string, unknown>) : {};
  const reps = Array.isArray(o["repairs"]) ? o["repairs"] : [];
  return reps.map((r) => {
    const x = r && typeof r === "object" ? (r as Record<string, unknown>) : {};
    return { title: str(x["title"]), category: str(x["category"]) || "General", contractor: str(x["contractor"]) || "General Contractor" };
  }).filter((r) => r.title.trim() !== "");
}

export interface ParsedInvoice { vendor: string; amount: number; desc: string; matchedIds: string[]; }

/** v1 selInvPick: invoice file (data URL) + the open repairs → parsed invoice. */
export async function parseInvoice(
  token: string | null, fileB64: string, mime: string, repairs: { id: string; title: string }[], signal?: AbortSignal,
): Promise<ParsedInvoice> {
  const j = await backendFetch(token, "/api/selling/invoice-parse", {
    method: "POST", body: { file_b64: fileB64, mime: mime || "application/pdf", repairs }, timeoutMs: 120000, signal,
  });
  const o = j && typeof j === "object" ? (j as Record<string, unknown>) : {};
  const matched = o["matched_ids"];
  return {
    vendor: str(o["vendor"]),
    amount: num(o["amount"]),
    desc: str(o["description"]),
    matchedIds: Array.isArray(matched) ? matched.map(str) : [],
  };
}

/** v1 selRemarksGen: POST the gathered facts to /api/selling/remarks and get
 * the AI-written MLS copy back. Payload field names match v1 exactly
 * (year_built / water_heater / renovation_highlights / sow_items). */
export async function generateRemarksAi(
  token: string | null, facts: RemarksFacts, sowItems: number,
  tone: RemarksTone, length: RemarksLength, notes: string, signal?: AbortSignal,
): Promise<string> {
  const j = await backendFetch(token, "/api/selling/remarks", {
    method: "POST",
    body: {
      facts: {
        address: facts.address, beds: facts.beds, baths: facts.baths, sqft: facts.sqft,
        year_built: facts.year, roof: facts.roof, hvac: facts.hvac, water_heater: facts.hwh,
        hoa: facts.hoa, renovation_highlights: facts.highlights, sow_items: sowItems,
      },
      tone, length, notes,
    },
    timeoutMs: 120000, signal,
  });
  const o = j && typeof j === "object" ? (j as Record<string, unknown>) : {};
  const text = str(o["text"]).trim();
  if (!text) throw new Error("The backend returned no remarks text");
  return text;
}

/** Read a file as a data URL (v1 _selB64) — the server accepts the whole URL. */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = () => resolve(String(rd.result));
    rd.onerror = () => reject(new Error("Could not read the file"));
    rd.readAsDataURL(file);
  });
}

/** Read a deal's selling slice from its edits (fields). */
export function repairsOf(fields: Record<string, unknown>): SelRepair[] {
  return (Array.isArray(fields["selRepairs"]) ? (fields["selRepairs"] as unknown[]) : []).map(narrowRepair);
}
export function invoicesOf(fields: Record<string, unknown>): SelInvoice[] {
  return (Array.isArray(fields["selInvoices"]) ? (fields["selInvoices"] as unknown[]) : []).map(narrowInvoice);
}
export function addendumsOf(fields: Record<string, unknown>): SelAddendum[] {
  return (Array.isArray(fields["selAddendums"]) ? (fields["selAddendums"] as unknown[]) : []).map((raw) => {
    const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    return { name: str(o["name"]) || "Addendum", n: num(o["n"]), at: num(o["at"]) };
  });
}
export function disclosureOf(fields: Record<string, unknown>): Record<string, string> {
  const d = fields["selDisclosure"];
  const out: Record<string, string> = {};
  if (d && typeof d === "object" && !Array.isArray(d)) for (const [k, v] of Object.entries(d as Record<string, unknown>)) out[k] = str(v);
  return out;
}
export function discNotesOf(fields: Record<string, unknown>): Record<string, string> {
  const d = fields["selDiscNotes"];
  const out: Record<string, string> = {};
  if (d && typeof d === "object" && !Array.isArray(d)) for (const [k, v] of Object.entries(d as Record<string, unknown>)) out[k] = str(v);
  return out;
}

/** Florida Seller's Property Disclosure question bank (v1 SEL_DISC, verbatim). */
export const SEL_DISC: [string, [string, string][]][] = [
  ["Roof", [["roof_leak", "Are you aware of any roof leaks, past or present?"], ["roof_repair", "Has the roof been repaired or replaced during your ownership?"], ["roof_age", "Is the roof more than 15 years old?"]]],
  ["HVAC & Systems", [["hvac_def", "Any defects with the heating or cooling systems?"], ["hwh_def", "Any problems with the water heater?"]]],
  ["Plumbing", [["plumb_leak", "Any past or present plumbing leaks?"], ["plumb_poly", "Does the home have polybutylene or galvanized piping?"], ["septic", "Any problems with the sewer line, septic system, or drainage?"]]],
  ["Electrical", [["elec_def", "Any defects in the electrical system (panel, wiring, outlets)?"], ["elec_alum", "Does the home contain aluminum branch wiring or a recalled panel?"]]],
  ["Structure & Foundation", [["struct_def", "Any structural, foundation, or settlement issues?"], ["struct_rep", "Any structural repairs performed during your ownership?"], ["sink", "Any knowledge of sinkhole activity or sinkhole insurance claims on the property or adjacent lots?"]]],
  ["Water Intrusion & Mold", [["flood", "Has the property ever flooded or had standing water on the lot?"], ["water_int", "Any water intrusion into the home (windows, doors, walls)?"], ["mold", "Any past or present mold or moisture problems?"]]],
  ["Termites & Pests", [["termite", "Any past or present termite or wood-destroying organism damage?"], ["termite_tx", "Is the property under a termite bond or has it been treated?"]]],
  ["Additions & Permits", [["permits", "Were any additions or alterations made without required permits?"], ["unpermitted", "Any open/expired permits or unpermitted structures (sheds, enclosures)?"]]],
  ["HOA & Deed", [["hoa", "Is the property subject to an HOA or CDD?"], ["hoa_viol", "Any pending HOA violations, assessments, or disputes?"], ["deed", "Any deed restrictions, easements, or encroachments affecting the property?"]]],
  ["Legal & Other", [["litigation", "Any pending litigation, insurance claims, or code violations affecting the property?"], ["lead", "Was the home built before 1978 (lead-based paint disclosure applies)?"], ["other", "Any other material defects a buyer should know about?"]]],
];

export function discCounts(disclosure: Record<string, string>): { answered: number; total: number } {
  let total = 0, answered = 0;
  for (const [, qs] of SEL_DISC) for (const [id] of qs) { total++; if (disclosure[id]) answered++; }
  return { answered, total };
}

const esc = (s: string): string => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));
const money = (n: number): string => moneyExact(n);

/** Open printable HTML in a new tab via a blob URL (robust vs document.write). */
export function openPrintable(html: string): boolean {
  try {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { URL.revokeObjectURL(url); return false; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch { return false; }
}

/** v1 selRepairReport — Repair Completion Report. */
export function completionReportHtml(address: string, repairs: SelRepair[], invoices: SelInvoice[], today: string): string {
  const done = repairs.filter((r) => r.st === "Completed").length;
  const cost = invoices.reduce((s, v) => s + (v.amount || 0), 0);
  const cats = [...new Set(repairs.map((r) => r.cat || "General"))];
  const style = "body{font-family:-apple-system,Segoe UI,Arial,sans-serif;max-width:760px;margin:32px auto;color:#0f172a;line-height:1.5}h1{font-size:22px;margin-bottom:2px}h2{font-size:15px;margin:22px 0 8px;border-bottom:2px solid #e2e8f0;padding-bottom:4px}.meta{color:#64748b;font-size:13px}.item{padding:7px 0;border-bottom:1px solid #f1f5f9;font-size:13.5px}.done{color:#059669;font-weight:600;font-size:11px;margin-left:6px}.pend{color:#d97706;font-weight:600;font-size:11px;margin-left:6px}.sub{color:#64748b;font-size:12px}.tot{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px 16px;margin:14px 0;font-size:14px}@media print{button{display:none}}";
  const cat = (c: string) => `<h2>${esc(c)}</h2>` + repairs.filter((r) => (r.cat || "General") === c).map((r) => `<div class="item">${esc(r.t)}<span class="${r.st === "Completed" ? "done" : "pend"}">${(r.st || "Pending").toUpperCase()}</span><div class="sub">${esc(r.who || "")}${r.cost ? " · " + money(r.cost) : ""}${r.desc ? " · " + esc(r.desc) : ""}</div></div>`).join("");
  const inv = invoices.length ? invoices.map((v) => `<div class="item"><b>${esc(v.vendor)}</b> — ${money(v.amount)}<div class="sub">${esc(v.desc || "")}${v.linked.length ? " · covers " + v.linked.length + " repair item(s)" : ""}</div></div>`).join("") : '<div class="sub">No invoices recorded.</div>';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Repair Completion Report — ${esc(address)}</title><style>${style}</style></head><body><h1>Repair Completion Report</h1><div class="meta">${esc(address)} · Generated ${esc(today)}</div><div class="tot"><b>${done} of ${repairs.length}</b> repair items completed (${repairs.length ? Math.round((done / repairs.length) * 100) : 0}%) · Documented invoice total: <b>${money(cost)}</b></div>${cats.map(cat).join("")}<h2>Invoices</h2>${inv}<br><button onclick="window.print()" style="padding:10px 18px;background:#2563eb;color:#fff;border:none;border-radius:8px;font-size:14px;cursor:pointer">Print / Save as PDF</button></body></html>`;
}

/** v1 selDiscPrint — Seller's Property Disclosure document. */
export function disclosureDocHtml(address: string, seller: string, date: string, disclosure: Record<string, string>, notes: Record<string, string>): string {
  const style = "body{font-family:-apple-system,Segoe UI,Arial,sans-serif;max-width:760px;margin:32px auto;color:#0f172a;line-height:1.45;font-size:13px}h1{font-size:19px;margin-bottom:2px}h2{font-size:14px;margin:18px 0 6px;border-bottom:1.5px solid #e2e8f0;padding-bottom:3px}.meta{color:#64748b;font-size:12px}.q{display:flex;justify-content:space-between;gap:16px;padding:5px 0;border-bottom:1px solid #f8fafc}.a{font-weight:700;white-space:nowrap}.a.Yes{color:#dc2626}.a.No{color:#059669}.a.U{color:#d97706}.note{color:#92400e;font-size:12px;margin:2px 0 4px;padding-left:10px;border-left:2px solid #fcd34d}.sig{margin-top:36px;display:flex;gap:40px}.sig div{flex:1;border-top:1px solid #0f172a;padding-top:4px;font-size:12px;color:#64748b}.disc{font-size:11px;color:#64748b;margin-top:18px}@media print{button{display:none}}";
  const sec = ([s, qs]: [string, [string, string][]]) => `<h2>${esc(s)}</h2>` + qs.map(([id, q]) => {
    const a = disclosure[id];
    const cls = a === "?" ? "U" : a || "";
    const label = a === "?" ? "UNKNOWN" : (a || "—").toUpperCase();
    const note = notes[id] && (a === "Yes" || a === "?") ? `<div class="note">${esc(notes[id]!)}</div>` : "";
    return `<div class="q"><span>${esc(q)}</span><span class="a ${cls}">${label}</span></div>${note}`;
  }).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>Seller's Property Disclosure — ${esc(address)}</title><style>${style}</style></head><body><h1>Seller's Property Disclosure</h1><div class="meta">${esc(address)} · Seller: ${esc(seller || "____________")} · Date: ${esc(date)}</div><p class="meta">Seller makes the following disclosures regarding the condition of the property, to the best of Seller's actual knowledge. "Yes" = the condition exists or existed; "No" = not to Seller's knowledge; "Unknown" = Seller has no knowledge.</p>${SEL_DISC.map(sec).join("")}<div class="disc">Seller acquired the property for renovation and resale and has owned it for a limited period; disclosures reflect Seller's actual knowledge only. Buyer is encouraged to conduct independent inspections.</div><div class="sig"><div>Seller signature / date</div><div>Buyer signature / date</div></div><br><button onclick="window.print()" style="padding:10px 18px;background:#2563eb;color:#fff;border:none;border-radius:8px;font-size:14px;cursor:pointer">Print / Save as PDF</button></body></html>`;
}
